import React, { FunctionComponent, useEffect, useState } from "react";

import ProductTemplateColumnsDropdown from "components/basic/dropdowns/product-template-columns-dropdown/ProductTemplateColumnsDropdown";
import {
  IProductTemplateDropdownChangeParams,
  ProductTemplateDropdown,
} from "components/basic/dropdowns/product-template/ProductTemplateDropdown";
import TimeUnitDropdown from "components/basic/dropdowns/time-unit-dropdown/TimeUnitDropdown";
import { DashboardEditorProps, IProductTemplateTableEditor } from "components/ComponentType";
import { IProductTemplateTableProps } from "components/dashboard/product-template-table/ProductTemplateTable";
import { InputNumber, InputNumberValueChangeParams } from "primereact/inputnumber";
import { InputSwitch, InputSwitchChangeParams } from "primereact/inputswitch";
import { InputText } from "primereact/inputtext";
import { MultiSelectChangeParams } from "primereact/multiselect";
import { T } from "services/lang/LanguageService";

const defaultProductTemplateEditorConfig: IProductTemplateTableEditor = {
  id: undefined,
  safeName: "",
  transposed: false,
  showTitle: true,
  updateFrequency: undefined,
  hiddenColumns: [],
  onlyShownColumns: [],
  itemsPerPage: undefined,
  overrideLoadingConfig: undefined,
  filter: undefined,
  defaultFilter: undefined,
  hideDates: false,
  showFilter: false,
  allowAddEntry: true,
  allowRowEdit: true,
  showTotals: false,
};

const ProductTemplateTableEditor = ({
  config,
  onConfigChange,
}: DashboardEditorProps<IProductTemplateTableEditor>) => {
  let conf: IProductTemplateTableEditor = config ?? defaultProductTemplateEditorConfig;
  if (Object.keys(conf).length === 0) {
    conf = defaultProductTemplateEditorConfig;
  }

  const [currentConfig, setCurrentConfig] = useState(conf);
  const [selectedTemplate, setSelectedTemplate] = useState<IProductTemplateDropdownChangeParams>({
    value: 0,
    refName: "",
  });
  const [overrideConfig, setOverrideConfig] = useState(!!conf.overrideLoadingConfig);

  useEffect(() => {
    onConfigChange(currentConfig);
  }, [currentConfig]);

  const setConfigPropertyState = (
    property: keyof IProductTemplateTableProps,
    value: string | number | boolean | null
  ) => {
    setCurrentConfig(prevState => ({ ...prevState, [property]: value }));
  };

  const handleConfigChange = (
    e: React.ChangeEvent<HTMLInputElement> | InputNumberValueChangeParams | InputSwitchChangeParams
  ) => {
    setConfigPropertyState(e.target.id as keyof IProductTemplateTableProps, e.target.value);
  };

  const safeNameSelectionHandler = (e: IProductTemplateDropdownChangeParams) => {
    setConfigPropertyState("safeName", e.refName);
    setCurrentConfig(prevState => ({ ...prevState, id: e.value }));
    setSelectedTemplate(e);
  };

  const hiddenShownColumnsDropdownHandler = (e: MultiSelectChangeParams) => {
    setConfigPropertyState(e.target.id as keyof IProductTemplateTableProps, e.target.value);
  };

  const loadingConfigHandler = (e: any) => {
    setCurrentConfig(prevState => ({
      ...prevState,
      overrideLoadingConfig: {
        ...prevState.overrideLoadingConfig,
        loadingConfig: {
          ...prevState.overrideLoadingConfig?.loadingConfig,
          [e.target.id]: !isNaN(e.target.value)
            ? e.target.value
            : e.target.value.toString().toUpperCase(),
        },
      },
    }));
  };

  const overrideConfigHandler = () => {
    if (overrideConfig) {
      setOverrideConfig(false);
      const configState = { ...currentConfig };
      delete configState.overrideLoadingConfig;
      setCurrentConfig(configState);
    } else {
      setOverrideConfig(true);
    }
  };

  useEffect(() => {
    setSelectedTemplate({ value: currentConfig.id!, refName: currentConfig.safeName });
  }, []);

  return (
    <div className="grid p-fluid">
      <div className="col-12 mb-3 flex flex-column">
        <label htmlFor="safeName">{T("word.safe-name")}</label>
        <ProductTemplateDropdown
          id="safeName"
          value={currentConfig.id}
          onChange={safeNameSelectionHandler}
        />
      </div>
      <div className="col-4 gap-2 flex flex-column justify-content-center">
        <label htmlFor="filter">{T("word.hidden-filter")}</label>
        <InputText id="filter" value={currentConfig.filter} onChange={handleConfigChange} />
      </div>
      <div className="col-4 gap-2 flex flex-column justify-content-center">
        <label htmlFor="defaultFilter">{T("word.default-filter")}</label>
        <InputText
          id="defaultFilter"
          value={currentConfig.defaultFilter}
          onChange={handleConfigChange}
        />
      </div>
      <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
        <label htmlFor="itemsPerPage">{T("component.items-per-page")}</label>
        <InputNumber
          id="itemsPerPage"
          value={currentConfig?.itemsPerPage}
          onValueChange={handleConfigChange}
        />
      </div>
      <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
        <label htmlFor="updateFrequency">{T("load-conf.update-frequency")}</label>
        <div className="p-inputgroup">
          <InputNumber
            id="updateFrequency"
            value={currentConfig?.updateFrequency as number}
            onValueChange={handleConfigChange}
          />
          <span className="p-inputgroup-addon">s</span>
        </div>
      </div>
      <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
        <label htmlFor="transposed">{T("component.transposed")}</label>
        <InputSwitch
          id="transposed"
          checked={currentConfig.transposed}
          onChange={handleConfigChange}
        />
      </div>
      <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
        <label htmlFor="showTitle">{T("chart-config.show-title")}</label>
        <InputSwitch
          id="showTitle"
          checked={currentConfig.showTitle}
          onChange={handleConfigChange}
        />
      </div>
      <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
        <label htmlFor="hideDates">{T("component.hide-dates")}</label>
        <InputSwitch
          id="hideDates"
          checked={currentConfig.hideDates}
          onChange={handleConfigChange}
        />
      </div>
      <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
        <label htmlFor="showFilter">{T("component.show-filter")}</label>
        <InputSwitch
          id="showFilter"
          checked={currentConfig.showFilter}
          onChange={handleConfigChange}
        />
      </div>
      <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
        <label htmlFor="allowAddEntry">{T("component.allow-add-entry")}</label>
        <InputSwitch
          id="allowAddEntry"
          checked={currentConfig.allowAddEntry}
          onChange={handleConfigChange}
        />
      </div>
      <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
        <label htmlFor="allowRowEdit">{T("component.allow-row-edit")}</label>
        <InputSwitch
          id="allowRowEdit"
          checked={currentConfig.allowRowEdit}
          onChange={handleConfigChange}
        />
      </div>
      <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
        <label htmlFor="showTotals">{T("component.show-totals")}</label>
        <InputSwitch
          id="showTotals"
          checked={currentConfig.showTotals}
          onChange={handleConfigChange}
        />
      </div>
      <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
        <label htmlFor="overrideConfig">{T("component.override-config")}</label>
        <InputSwitch
          id="overrideConfig"
          checked={overrideConfig}
          onChange={overrideConfigHandler}
        />
      </div>
      {selectedTemplate.refName && (
        <>
          <div className="col-3 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="hiddenColumns">{T("component.hidden-columns")}</label>
            <ProductTemplateColumnsDropdown
              id="hiddenColumns"
              value={Array.isArray(currentConfig.hiddenColumns) ? currentConfig.hiddenColumns : []}
              className="w-full"
              onChange={hiddenShownColumnsDropdownHandler}
              selectedTemplate={selectedTemplate}
            />
          </div>
          <div className="col-3 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="onlyShownColumns">{T("component.only-show-columns")}</label>
            <ProductTemplateColumnsDropdown
              id="onlyShownColumns"
              value={
                Array.isArray(currentConfig.onlyShownColumns) ? currentConfig.onlyShownColumns : []
              }
              className="w-full"
              onChange={hiddenShownColumnsDropdownHandler}
              selectedTemplate={selectedTemplate}
              disabled={currentConfig.hiddenColumns && currentConfig.hiddenColumns?.length > 0}
            />
          </div>
        </>
      )}
      {overrideConfig && (
        <>
          <div className="col-4 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="period">{T("load-conf.period")}</label>
            <div className="p-inputgroup">
              <InputNumber
                id="period"
                value={currentConfig.overrideLoadingConfig?.loadingConfig?.period}
                onValueChange={loadingConfigHandler}
              />
              <TimeUnitDropdown
                id="periodUnit"
                className="p-inputgroup-addon p-0"
                value={currentConfig.overrideLoadingConfig?.loadingConfig?.periodUnit}
                onChange={loadingConfigHandler}
              />
            </div>
          </div>
          <div className="col-4 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="offset">{T("load-conf.offset")}</label>
            <div className="p-inputgroup">
              <InputNumber
                id="offset"
                value={currentConfig.overrideLoadingConfig?.loadingConfig?.offset}
                onValueChange={loadingConfigHandler}
              />
              <TimeUnitDropdown
                id="offsetUnit"
                className="p-inputgroup-addon p-0"
                value={currentConfig.overrideLoadingConfig?.loadingConfig?.offsetUnit!}
                onChange={loadingConfigHandler}
              />
            </div>
          </div>
          <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="defaultConfigUnit">{T("load-conf.default-config-unit")}</label>
            <TimeUnitDropdown
              id="defaultConfigUnit"
              value={currentConfig.overrideLoadingConfig?.loadingConfig?.defaultConfigUnit}
              onChange={loadingConfigHandler}
            />
          </div>
          <div className="col-2 gap-2 flex flex-column justify-content-center align-items-center">
            <label htmlFor="truncateStartUnit">{T("load-conf.truncate-start-unit")}</label>
            <TimeUnitDropdown
              id="truncateStartUnit"
              value={currentConfig?.overrideLoadingConfig?.loadingConfig?.truncateStartUnit}
              onChange={loadingConfigHandler}
            />
          </div>
        </>
      )}
    </div>
  );
};

export default ProductTemplateTableEditor as FunctionComponent;
